import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, AlertTriangle, Megaphone, FileWarning, CalendarDays, Bell, HandHeart, X, Facebook, Youtube, Twitter, Instagram, Mail, FileDown, Star } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Button } from './ui/button';
import { api, fileUrl, API } from '../lib/api';
import { FACEBOOK_PAGE_URL, FACEBOOK_REVIEWS_URL } from '../lib/social';
import { ChatWidget } from './ChatWidget';
import { SubscribeForm } from './Shared';
import { CvFooterLink } from './CvDownload';
import { useNavMenu, DesktopNavMenus, MobileNavAccordion } from './NavMenus';
import { SiteSearch } from './SiteSearch';
import { usePageContent } from '../lib/pageContent';
import { EditModeProvider, EditModeControl } from './EditMode';
import { AnnouncementTicker } from './AnnouncementTicker';

const SEVERITY_STYLES = {
  info: 'bg-slate-100 border-slate-300 text-slate-900',
  warning: 'bg-amber-50 border-amber-300 text-amber-950',
  urgent: 'bg-red-50 border-red-300 text-red-950',
};

export const PublicLayout = () => {
  const navGroups = useNavMenu();
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState([]);
  const [settings, setSettings] = useState({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get('/alerts/active').then((r) => setAlerts(r.data || [])).catch(() => {});
    api.get('/settings').then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  // Header: transparent with a soft white fade at the very top of the page,
  // switching to a solid white bar with a divider once the visitor scrolls.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll handling: jump to the section anchor when a link has a #hash
  // (retrying briefly so data-loaded sections are found), otherwise start at
  // the top of the page. Sticky-header offset comes from the global
  // scroll-margin-top rule in index.css.
  useEffect(() => {
    setDrawerOpen(false);
    if (location.hash) {
      const id = location.hash.slice(1);
      const jump = () => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return !!el;
      };
      const t1 = setTimeout(() => { if (!jump()) setTimeout(jump, 500); }, 80);
      return () => clearTimeout(t1);
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash, location.key]);

  const site = settings.site || {};
  const footer = settings.footer || {};
  const social = settings.social_links || {};
  const visibleAlerts = alerts.filter((a) => !dismissed.includes(a.id));

  // Admin -> Page Content: footer link columns + CTA button labels
  // (null = built-in approved wording below stays unchanged)
  const pageContent = usePageContent();
  const fb = pageContent.footer_buttons;
  const takeActionLinks = (fb?.take_action?.length ? fb.take_action : [
    { label: 'Report an Issue', url: '/issue-tracker/report' }, { label: 'Track My Issue', url: '/issue-tracker/track' },
    { label: 'Request a Sign', url: '/request-a-sign' }, { label: 'Volunteer', url: '/volunteer' },
    { label: 'Donate', url: '/donate' }, { label: 'Contact Steve', url: '/contact' },
    { label: 'How to Have Your Say', url: '/have-your-say' }, { label: 'Public Alerts', url: '/alerts' },
  ]).filter((l) => l.show !== false);
  // Footer navigation column: mirrors the v3 main menu (admin-editable
  // through the same Page Content "stay informed" list override)
  const stayInformedLinks = (fb?.stay_informed?.length ? fb.stay_informed : [
    { label: 'Home', url: '/home' }, { label: 'Request a Sign', url: '/request-a-sign' },
    { label: 'Election Event List', url: '/elections/events' }, { label: 'PITA', url: '/issue-tracker' },
    { label: 'Media', url: '/media' }, { label: 'About Steve', url: '/about' },
    { label: 'Platform', url: '/platform' }, { label: 'Reviews', url: '/reviews' },
    { label: 'Resources', url: '/resources' }, { label: 'Contact', url: '/contact' },
    { label: 'Donate', url: '/donate' },
  ]).filter((l) => l.show !== false);
  const drawerReportLabel = fb?.drawer_report_label || 'Report a Ward 8 Issue';

  return (
    <EditModeProvider>
    <div className="min-h-screen flex flex-col bg-[#F6F8FB] public-page-bottom-pad">
      {/* Soft launch banner */}
      {site.soft_launch_banner && (
        <div className="bg-[#FFF7E6] border-b border-[#F2D08A] px-4 py-2.5" data-testid="soft-launch-banner">
          <div className="max-w-6xl mx-auto flex items-start gap-2 text-sm text-[#7A4E00]">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p className="flex-1 font-semibold">{site.soft_launch_banner_text || 'The Vote Steve Lesperance campaign site is being prepared for public launch. Some data and media may still be under review.'}</p>
          </div>
        </div>
      )}

      {/* Alert banners */}
      {visibleAlerts.map((a) => (
        <div key={a.id} className={`border-b px-4 py-2.5 ${SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info}`} data-testid="site-alert-banner">
          <div className="max-w-6xl mx-auto flex items-start gap-2 text-sm">
            {a.severity === 'urgent' ? <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> : <Megaphone className="h-4 w-4 mt-0.5 shrink-0" />}
            <p className="flex-1"><strong>{a.title}</strong>{a.message ? `: ${a.message}` : ''}</p>
            <button onClick={() => setDismissed((d) => [...d, a.id])} aria-label="Dismiss alert" className="p-0.5" data-testid="alert-dismiss-button"><X className="h-4 w-4" /></button>
          </div>
        </div>
      ))}

      {/* Header: transparent at top -> solid white on scroll */}
      <header className={`sticky top-0 z-40 ${scrolled ? 'site-header-scrolled' : 'site-header-top'}`} data-testid="site-header">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-[76px]">
            {/* Campaign logo artwork: always routes to /home (the entrance stays at /) */}
            <Link to="/home" className="flex items-center shrink-0 mr-3" data-testid="site-logo-link" aria-label="Steve Lesperance for CKL Councillor Ward 8, go to homepage">
              <img
                src="/branding/ElectSteveLesperance2.png"
                alt="Steve Lesperance for CKL Councillor Ward 8"
                className="h-11 lg:h-14 w-auto"
                data-testid="header-logo-image"
              />
            </Link>

            {/* Desktop nav: grouped dropdowns (editable in Admin → Menu Manager) */}
            <DesktopNavMenus groups={navGroups} />

            <div className="flex items-center gap-1 sm:gap-2">
              <SiteSearch />
              {/* GOT AN ISSUE? -> hover: PUT IT IN PITA! (red). Direct route to the report wizard. */}
              <Link to="/issue-tracker/report" className="got-issue-btn hidden xl:inline-flex btn-press" data-testid="nav-got-an-issue-button" aria-label="Got an issue? Put it in PITA. Report a Ward 8 issue">
                <span className="gi-default">GOT AN ISSUE?</span>
                <span className="gi-hover" aria-hidden="true">PUT IT IN PITA!</span>
              </Link>
              <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                <SheetTrigger asChild>
                  <button className="p-2.5 rounded-lg text-[#1a2744] hover:bg-slate-100" aria-label="Open menu" data-testid="site-header-menu-button">
                    <Menu className="h-6 w-6" />
                  </button>
                </SheetTrigger>
                <SheetContent side="right" className="bg-white text-[#0f172a] border-l border-slate-200 w-[320px] sm:w-[340px] overflow-y-auto p-0">
                  <div className="p-5 border-b border-slate-200">
                    <img src="/branding/ElectSteveLesperance2.png" alt="Steve Lesperance for CKL Councillor Ward 8" className="w-48 h-auto" data-testid="drawer-logo-image" />
                  </div>
                  <div className="p-3 border-b border-slate-200">
                    <Button asChild className="w-full bg-[#b91c1c] hover:bg-[#991b1b] text-white font-bold h-11 btn-press" data-testid="mobile-drawer-report-issue-button">
                      <Link to="/issue-tracker/report" onClick={() => setDrawerOpen(false)}>{drawerReportLabel}</Link>
                    </Button>
                  </div>
                  <nav className="px-1 pb-6" aria-label="All pages">
                    <MobileNavAccordion groups={navGroups} onNavigate={() => setDrawerOpen(false)} />
                  </nav>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Site-wide announcement ticker (Phase 98): thin blue bar directly
          below the main navigation; hidden entirely when off/empty. */}
      <AnnouncementTicker />

      {/* Page content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer: black campaign band with the white logo */}
      <footer className="bg-[#0b0f17] text-white mt-12 relative pb-16 md:pb-0">
        <div aria-hidden="true" className="absolute inset-0 noise-overlay opacity-40" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1">
              <img src="/branding/ElectSteveLesperance2-WHITE.png" alt="Steve Lesperance for CKL Councillor Ward 8" className="w-56 h-auto" data-testid="footer-logo-image" />
              <p className="text-sm font-black text-white mt-5 tracking-wide" data-testid="footer-accountability-line">ACCOUNTABILITY YOU CAN SEE.</p>
              <p className="text-xs font-bold text-slate-400 mt-1.5" data-testid="footer-hope-line">Lesperance is the French word for Hope</p>
              <p className="text-sm text-slate-400 mt-4 leading-relaxed">{footer.about || ''}</p>
              <div className="flex gap-3 mt-4">
                {social.facebook && <a href={social.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="p-3 sm:p-2 bg-white/10 rounded-lg hover:bg-white/20 inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"><Facebook className="h-4 w-4" /></a>}
                {social.youtube && <a href={social.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="p-3 sm:p-2 bg-white/10 rounded-lg hover:bg-white/20 inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"><Youtube className="h-4 w-4" /></a>}
                {social.x_twitter && <a href={social.x_twitter} target="_blank" rel="noopener noreferrer" aria-label="X" className="p-3 sm:p-2 bg-white/10 rounded-lg hover:bg-white/20 inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"><Twitter className="h-4 w-4" /></a>}
                {social.instagram && <a href={social.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="p-3 sm:p-2 bg-white/10 rounded-lg hover:bg-white/20 inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"><Instagram className="h-4 w-4" /></a>}
                {site.contact_email && <a href={`mailto:${site.contact_email}`} aria-label="Email" className="p-3 sm:p-2 bg-white/10 rounded-lg hover:bg-white/20 inline-flex items-center justify-center min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"><Mail className="h-4 w-4" /></a>}
              </div>
              {/* Facebook page + reviews: always present in the footer (Phase 123) */}
              <div className="mt-4 flex flex-col gap-1" data-testid="footer-facebook-links">
                <a href={FACEBOOK_PAGE_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white py-1.5 min-h-11 sm:min-h-0" data-testid="footer-facebook-page-link"><Facebook className="h-3.5 w-3.5" /> Vote Steve Lesperance on Facebook</a>
                <a href={FACEBOOK_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white py-1.5 min-h-11 sm:min-h-0" data-testid="footer-facebook-reviews-link"><Star className="h-3.5 w-3.5" /> Read &amp; Leave Facebook Reviews</a>
                <Link to="/reviews" className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white py-1.5 min-h-11 sm:min-h-0" data-testid="footer-site-reviews-link">Reviews on this site</Link>
              </div>
            </div>
            <div>
              <p className="font-bold text-xs uppercase tracking-[0.2em] text-white/60 mb-3">Take Action</p>
              {takeActionLinks.map((l) => (
                <Link key={l.url + l.label} to={l.url} className="block text-sm text-slate-300 hover:text-white py-2.5 sm:py-1.5 min-h-11 sm:min-h-0 flex items-center">{l.label}</Link>
              ))}
              {settings.campaign_flyer?.enabled !== false && settings.campaign_flyer?.pdf_url && (
                <>
                  <a href={`${API}/flyer/download/pdf?loc=footer`} download="ElectSteveLesperance-2PageFlyer-01.pdf" className="inline-flex items-center gap-1.5 text-sm font-bold text-white underline decoration-white/30 underline-offset-4 hover:decoration-white py-2.5 sm:py-1.5 min-h-11 sm:min-h-0" data-testid="footer-flyer-download-link">
                    <FileDown className="h-3.5 w-3.5" /> Download Steve's 2-Page Flyer PDF
                  </a>
                  <a href={`${API}/flyer/download/docx?loc=footer`} download="ElectSteveLesperance-2PageFlyer-01.docx" className="flex items-center gap-1.5 text-sm font-bold text-white underline decoration-white/30 underline-offset-4 hover:decoration-white py-2.5 sm:py-1.5 min-h-11 sm:min-h-0" data-testid="footer-flyer-download-docx-link">
                    <FileDown className="h-3.5 w-3.5" /> Download Steve's 2-Page Flyer DOCX
                  </a>
                </>
              )}
              <div><CvFooterLink /></div>
            </div>
            <div>
              <p className="font-bold text-xs uppercase tracking-[0.2em] text-white/60 mb-3">Explore the Site</p>
              {stayInformedLinks.map((l) => (
                <Link key={l.url + l.label} to={l.url} className="block text-sm text-slate-300 hover:text-white py-2.5 sm:py-1.5 min-h-11 sm:min-h-0 flex items-center">{l.label}</Link>
              ))}
            </div>
            <div data-testid="site-footer-newsletter">
              <p className="font-bold text-xs uppercase tracking-[0.2em] text-white/60 mb-3">Subscribe for Alerts</p>
              <p className="text-sm text-slate-400 mb-3">Get campaign updates and public alerts for Ward 8.</p>
              <SubscribeForm compact testId="footer-subscribe-form" />
            </div>
          </div>
          <div className="border-t border-white/10 mt-10 pt-6">
            <p className="text-xs text-slate-500 leading-relaxed" data-testid="site-footer-disclaimer">{footer.disclaimer || ''}</p>
            <p className="text-xs text-slate-600 mt-3">© {new Date().getFullYear()} {footer.copyright || 'Campaign to Elect Steve Lesperance, Ward 8'} · <Link to="/admin/login" className="hover:text-slate-400">Admin</Link></p>
          </div>
        </div>
      </footer>

      {/* Sticky mobile quick-action bar */}
      <nav className="mobile-quickbar fixed bottom-0 inset-x-0 z-40 md:hidden bg-white/95 backdrop-blur border-t border-slate-200" aria-label="Quick actions" data-testid="mobile-quickbar">
        <div className="grid grid-cols-5">
          {[
            { label: 'Report', icon: FileWarning, to: '/issue-tracker/report', testid: 'quickbar-report-issue', accent: true },
            { label: 'Meetings', icon: CalendarDays, to: '/meetings', testid: 'quickbar-meetings' },
            { label: 'Alerts', icon: Bell, to: '/alerts', testid: 'quickbar-alerts' },
            { label: 'Volunteer', icon: HandHeart, to: '/volunteer', testid: 'quickbar-volunteer' },
          ].map(({ label, icon: Icon, to, testid, accent }) => (
            <Link key={to} to={to} data-testid={testid} className={`flex flex-col items-center justify-center min-h-14 py-2 gap-0.5 ${location.pathname === to ? 'text-[#1a2744]' : accent ? 'text-[#b91c1c]' : 'text-slate-500'}`}>
              <Icon className="h-5 w-5" />
              <span className={`text-[10px] font-bold ${location.pathname === to ? 'border-b-2 border-[#b91c1c]' : ''}`}>{label}</span>
            </Link>
          ))}
          <button onClick={() => setDrawerOpen(true)} data-testid="quickbar-menu" className="flex flex-col items-center justify-center min-h-14 py-2 gap-0.5 text-slate-500">
            <Menu className="h-5 w-5" />
            <span className="text-[10px] font-bold">Menu</span>
          </button>
        </div>
      </nav>

      <ChatWidget />
      {/* Admin-only on-page editing control (Phase 96); invisible to visitors */}
      <EditModeControl />
    </div>
    </EditModeProvider>
  );
};
